import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { AppCore } from '@core/bootstrap'
import type { Category, Product } from '@core/db/models'
import {
  cx,
  fieldLabel,
  hint,
  input,
  modalSm,
  overlay,
  table,
  tableWrap,
  td,
  tdNum,
  th,
  thNum
} from '@ui/classes'
import { money } from '@core/utils/format'
import { useIsMobile } from '@core/utils/useIsMobile'
import {
  badgeNo,
  badgePosCreated,
  badgeWeight,
  badgeYes,
  credInput,
  errBanner,
  filterBar,
  filterChip,
  okBanner
} from '@ui/admin'
import { Close, Marking, Scales, Trash } from '@ui/icons'
import { IconButton } from '@ui/IconButton'
import { syncService } from '@core/services/sync/SyncService'
import { hasPendingEdit } from '@core/services/catalog/pendingProductEdits'
import { useAuthToken } from './useAuthToken'
import { CLOUD_API } from './shared'

/** Классы инлайн-полей таблицы каталога. */
const nameInput =
  'w-full min-w-[120px] rounded-md border border-lineStrong px-1.5 py-1 text-small text-ink'
const barcodeInput =
  'w-full rounded-md border border-lineStrong px-1.5 py-1 font-mono text-tiny text-ink'
const categorySelect =
  'w-full rounded-md border border-lineStrong px-1.5 py-1 text-small text-ink'
/** Числовые поля: ширина по колонке, а не фиксированные 96px. */
const priceField =
  'w-full min-w-[56px] rounded-md border border-lineStrong px-1 py-1 text-right text-small tabular-nums text-ink'

/** Доля «Наименование + Категория» и границы ползунка. */
const FLEX_SHARE = 55
const MIN_NAME_SHARE = 18
const MAX_NAME_SHARE = FLEX_SHARE - MIN_NAME_SHARE
const NAME_SHARE_KEY = 'admin.catalog.nameShare'
/** Лимит строк в DOM: 858 товаров с инпутами тормозят окно. */
const PAGE_SIZE = 200
/** Порядок полей строки для Enter. */
const FIELD_ORDER = ['name', 'category', 'barcode', 'price', 'minStock'] as const
type EditableField = (typeof FIELD_ORDER)[number]

/** Опция фильтра/селектора категории: верхний уровень или конкретный лист. */
interface CategoryChoice {
  value: string
  label: string
  parent: string
  leaf: string | null
}

interface CreateDraft {
  name: string
  price: string
  barcode: string
  categoryValue: string
  isWeight: boolean
  requiresMarking: boolean
}

const EMPTY_CREATE: CreateDraft = {
  name: '',
  price: '',
  barcode: '',
  categoryValue: 'all',
  isWeight: false,
  requiresMarking: false
}

/** Перейти к следующему полю строки после Enter. */
function focusNextField(rowId: number, current: EditableField): void {
  const index = FIELD_ORDER.indexOf(current)
  const next = FIELD_ORDER[index + 1]
  if (!next) return
  document
    .querySelector<HTMLElement>(`[data-row="${rowId}"][data-field="${next}"]`)
    ?.focus()
}

/**
 * Таблица товаров активной версии: адаптивные колонки, фильтры, инлайн-правка,
 * корзина мягко удалённых товаров и создание новой позиции.
 *
 * На мобиле таблица заменяется карточками-аккордеонами: тап раскрывает форму
 * со всеми полями в столбик.
 */
export function ActiveCatalogSection({
  core,
  refreshKey
}: {
  core: AppCore
  refreshKey: number
}) {
  const { catalog } = core
  const token = useAuthToken()
  const isMobile = useIsMobile(900)
  const [products, setProducts] = useState<Product[]>([])
  const [tree, setTree] = useState<Category[]>([])
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Фильтры таблицы каталога (клиентская фильтрация без перезагрузки).
  const [filterText, setFilterText] = useState('')
  const [filterChoice, setFilterChoice] = useState('all')
  const [filterWeight, setFilterWeight] = useState(false)
  const [filterMarked, setFilterMarked] = useState(false)
  const [filterOnPos, setFilterOnPos] = useState(false)
  const [filterInactive, setFilterInactive] = useState(false)
  /** Общий порог магазина — подсказка в поле «Мин. остаток». */
  const [storeThreshold, setStoreThreshold] = useState(3)
  /** Корзина: мягко удалённые товары. */
  const [trashMode, setTrashMode] = useState(false)
  const [trash, setTrash] = useState<Product[]>([])
  const [visibleLimit, setVisibleLimit] = useState(PAGE_SIZE)
  /** Раскрытая карточка на мобиле. */
  const [expandedId, setExpandedId] = useState<number | null>(null)
  /** Раскрытое поле «Наименование» в таблице. */
  const [focusedNameId, setFocusedNameId] = useState<number | null>(null)
  /** Модалка создания товара. */
  const [createOpen, setCreateOpen] = useState(false)
  const [createDraft, setCreateDraft] = useState<CreateDraft>(EMPTY_CREATE)
  const [createBusy, setCreateBusy] = useState(false)
  /** Доля «Наименование» внутри гибкой части (в процентах таблицы). */
  const [nameShare, setNameShare] = useState<number>(() => {
    try {
      const raw = window.localStorage.getItem(NAME_SHARE_KEY)
      const value = Number(raw)
      if (Number.isFinite(value) && value >= MIN_NAME_SHARE && value <= MAX_NAME_SHARE) {
        return value
      }
    } catch {
      // Приватный режим: остаётся значение по умолчанию.
    }
    return 33
  })
  const tableRef = useRef<HTMLTableElement>(null)
  const dragRef = useRef<{ startX: number; startShare: number; width: number } | null>(null)

  const refresh = useCallback(async () => {
    const [list, threshold, categories, deleted] = await Promise.all([
      catalog.listActiveProducts(),
      catalog.getLowStockThreshold(),
      catalog.listCategoryTree(),
      catalog.listDeletedProducts()
    ])
    setProducts(list)
    setStoreThreshold(threshold)
    setTree(categories)
    setTrash(deleted)
    setVisibleLimit(PAGE_SIZE)
  }, [catalog])

  useEffect(() => {
    void refresh().catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
  }, [refresh, refreshKey])

  useEffect(() => {
    try {
      window.localStorage.setItem(NAME_SHARE_KEY, String(nameShare))
    } catch {
      // storage недоступен — не критично.
    }
  }, [nameShare])

  const parentOf = (p: Product): string =>
    p.categoryParent?.trim() || p.category?.trim() || 'Общее'
  const leafOf = (p: Product): string => p.category?.trim() || 'Общее'

  /** Единый список «верхняя категория + её подкатегории». */
  const choices = useMemo<CategoryChoice[]>(() => {
    const parents = new Map<string, Set<string>>()
    for (const category of tree) {
      if (category.parentName == null) {
        if (!parents.has(category.name)) parents.set(category.name, new Set())
      } else {
        if (!parents.has(category.parentName)) parents.set(category.parentName, new Set())
        parents.get(category.parentName)?.add(category.name)
      }
    }
    for (const p of products) {
      const parent = parentOf(p)
      const leaf = leafOf(p)
      if (!parents.has(parent)) parents.set(parent, new Set())
      if (leaf !== parent) parents.get(parent)?.add(leaf)
    }

    const result: CategoryChoice[] = []
    const sortedParents = [...parents.keys()].sort((a, b) => a.localeCompare(b, 'ru'))
    for (const parent of sortedParents) {
      const leaves = [...(parents.get(parent) ?? [])].sort((a, b) =>
        a.localeCompare(b, 'ru')
      )
      result.push({
        value: String(result.length),
        label: leaves.length > 0 ? `${parent} — вся категория` : parent,
        parent,
        leaf: null
      })
      for (const leaf of leaves) {
        result.push({ value: String(result.length), label: `— ${leaf}`, parent, leaf })
      }
    }
    return result
  }, [tree, products])

  const selected = filterChoice === 'all' ? null : choices[Number(filterChoice)] ?? null

  const matchesFilters = useCallback(
    (p: Product): boolean => {
      if (selected && parentOf(p) !== selected.parent) return false
      if (selected?.leaf && leafOf(p) !== selected.leaf) return false
      if (filterWeight && !(p.isWeight || p.unit === 'кг')) return false
      if (filterMarked && !p.requiresMarking) return false
      if (filterOnPos && !p.isCreatedOnPos) return false
      if (filterInactive && p.isActive !== false) return false
      const q = filterText.trim().toLowerCase()
      if (q) {
        const hay = `${p.name} ${p.barcode ?? ''} ${p.price}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    },
    [selected, filterWeight, filterMarked, filterOnPos, filterInactive, filterText]
  )

  const filteredProducts = useMemo(
    () => products.filter(matchesFilters),
    [products, matchesFilters]
  )
  const filteredTrash = useMemo(() => trash.filter(matchesFilters), [trash, matchesFilters])
  const rows = trashMode ? filteredTrash : filteredProducts
  const shownRows = rows.slice(0, visibleLimit)

  /** Ширины автоколонок: максимум из заголовка и содержимого. */
  const barcodeCh = useMemo(() => {
    const maxLen = rows.reduce((max, p) => Math.max(max, (p.barcode ?? '').length), 0)
    return Math.max('Штрихкод'.length + 1, maxLen + 1)
  }, [rows])
  const priceCh = useMemo(() => {
    const maxLen = rows.reduce((max, p) => Math.max(max, money(p.price).length), 0)
    return Math.max('Цена, ₽'.length + 1, maxLen + 1)
  }, [rows])

  const onResizeStart = (event: React.PointerEvent) => {
    event.preventDefault()
    const width = tableRef.current?.getBoundingClientRect().width ?? 0
    if (width === 0) return
    dragRef.current = { startX: event.clientX, startShare: nameShare, width }
    const onMove = (e: PointerEvent) => {
      const state = dragRef.current
      if (!state) return
      const delta = ((e.clientX - state.startX) / state.width) * 100
      const next = Math.min(
        MAX_NAME_SHARE,
        Math.max(MIN_NAME_SHARE, state.startShare + delta)
      )
      setNameShare(next)
    }
    const onUp = () => {
      dragRef.current = null
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const resetWidths = () => setNameShare(33)

  /** Облачный вызов (корзина, создание товара). */
  const cloudPost = useCallback(
    async (path: string, body: unknown): Promise<unknown> => {
      if (!token) throw new Error('Нужен вход в облако')
      const response = await fetch(`${CLOUD_API}/catalog${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      })
      const payload = (await response.json().catch(() => ({}))) as {
        error?: string
      }
      if (!response.ok) {
        if (payload.error === 'barcode_exists') {
          throw new Error('Товар с таким штрихкодом уже есть в каталоге')
        }
        throw new Error(`Не удалось выполнить: HTTP ${response.status}`)
      }
      return payload
    },
    [token]
  )

  const reportSaved = async (
    product: Product,
    text: string,
    queued: boolean
  ): Promise<void> => {
    if (!queued) {
      setMessage(`${text} (только на этом устройстве — товар ещё не в облаке).`)
      return
    }
    if (token) await syncService.pushPendingEdits(token)
    const left = product.cloudId != null ? await hasPendingEdit(product.cloudId) : false
    setMessage(left ? `${text}. Отправим в облако при связи.` : text)
  }

  const onPriceBlur = async (product: Product, raw: string) => {
    const next = Number(raw.replace(',', '.'))
    if (!Number.isFinite(next) || next < 0 || product.id == null) {
      await refresh()
      return
    }
    if (next === product.price) return
    try {
      const { queued } = await catalog.updatePrice(product.id, next)
      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, price: next } : p))
      )
      await reportSaved(product, `Цена «${product.name}»: ${money(next)}`, queued)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
      await refresh()
    }
  }

  const saveFields = async (
    product: Product,
    patch: {
      name?: string
      barcode?: string
      category?: string
      categoryParent?: string
      requiresMarking?: boolean
    }
  ) => {
    if (product.id == null) return
    if (patch.name !== undefined && patch.name.trim() === product.name) return
    if (patch.barcode !== undefined && patch.barcode.trim() === (product.barcode ?? '')) {
      return
    }
    try {
      const { queued } = await catalog.updateProductFields(product.id, patch)
      await refresh()
      const label =
        patch.name !== undefined
          ? `Наименование «${patch.name.trim()}»`
          : patch.category !== undefined
            ? `Категория «${product.name}»: ${patch.categoryParent ?? patch.category}`
            : patch.barcode !== undefined
              ? `Штрихкод «${product.name}»: ${patch.barcode.trim() || 'пусто'}`
              : patch.requiresMarking
                ? `Маркировка «${product.name}»: да`
                : `Маркировка «${product.name}»: нет`
      await reportSaved(product, label, queued)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
      await refresh()
    }
  }

  const onActiveToggle = async (product: Product, active: boolean) => {
    if (product.id == null) return
    try {
      await catalog.setProductActive(product.id, active)
      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, isActive: active } : p))
      )
      setMessage(
        active
          ? `Товар «${product.name}» активирован`
          : `Товар «${product.name}» деактивирован: с кассы скрыт, номер сохранён`
      )
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const onMinStockBlur = async (product: Product, raw: string) => {
    if (product.id == null) return
    const trimmed = raw.trim()
    const next = trimmed === '' ? null : Number(trimmed.replace(',', '.'))
    if (next != null && (!Number.isFinite(next) || next < 0)) {
      await refresh()
      return
    }
    if ((next ?? undefined) === (product.minStock ?? undefined)) return
    try {
      const { queued } = await catalog.updateMinStock(product.id, next)
      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, minStock: next ?? undefined } : p))
      )
      const valueText =
        next == null ? `общий порог магазина (${storeThreshold})` : `${next} ед.`
      await reportSaved(product, `Мин. остаток «${product.name}»: ${valueText}`, queued)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
      await refresh()
    }
  }

  const moveToTrash = async (product: Product) => {
    if (product.id == null) return
    setError(null)
    try {
      if (token && product.cloudId != null) {
        await cloudPost('/products/delete', { product_id: product.cloudId })
      }
      await catalog.deleteProduct(product.id)
      setMessage(`Товар «${product.name}» в корзине — на кассе скрыт`)
      await refresh()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const restoreFromTrash = async (product: Product) => {
    if (product.id == null) return
    setError(null)
    try {
      if (token && product.cloudId != null) {
        await cloudPost('/products/restore', { product_id: product.cloudId })
      }
      await catalog.restoreProduct(product.id)
      setMessage(`Товар «${product.name}» восстановлен`)
      await refresh()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const purgeTrash = async () => {
    if (trash.length === 0) return
    if (
      !window.confirm(
        `Удалить безвозвратно ${trash.length} товар(ов) из корзины? Восстановить их будет нельзя.`
      )
    ) {
      return
    }
    setError(null)
    try {
      if (token) await cloudPost('/products/purge', {})
      const removed = await catalog.purgeDeleted()
      setMessage(`Корзина очищена: удалено ${removed}`)
      await refresh()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  /** Создание товара из админки. */
  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    const name = createDraft.name.trim()
    const price = Number(createDraft.price.replace(',', '.'))
    if (!name) {
      setError('Укажите наименование товара')
      return
    }
    if (!Number.isFinite(price) || price < 0) {
      setError('Цена должна быть неотрицательным числом')
      return
    }
    const choice =
      createDraft.categoryValue === 'all'
        ? null
        : choices[Number(createDraft.categoryValue)] ?? null
    const category = choice ? choice.leaf ?? choice.parent : 'Общее'
    const categoryParent = choice ? choice.parent : 'Общее'

    setCreateBusy(true)
    setError(null)
    try {
      let cloudId: number | undefined
      if (token) {
        const payload = (await cloudPost('/products', {
          name,
          price,
          barcode: createDraft.barcode.trim(),
          category,
          category_parent: categoryParent,
          requires_marking: createDraft.requiresMarking,
          is_weight: createDraft.isWeight,
          unit: createDraft.isWeight ? 'кг' : 'шт'
        })) as { product_id?: number }
        cloudId = payload.product_id
      }
      await catalog.createProductAdmin({
        name,
        price,
        barcode: createDraft.barcode,
        category,
        categoryParent,
        requiresMarking: createDraft.requiresMarking,
        isWeight: createDraft.isWeight,
        cloudId
      })
      setCreateOpen(false)
      setCreateDraft(EMPTY_CREATE)
      setMessage(
        token ? `Товар «${name}» создан` : `Товар «${name}» создан только на этом устройстве`
      )
      await refresh()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setCreateBusy(false)
    }
  }

  /** Селектор категории товара (общий для таблицы и карточек). */
  const renderCategorySelect = (p: Product) => (
    <select
      data-row={p.id}
      data-field="category"
      value={`${parentOf(p)}\u0000${leafOf(p)}`}
      onChange={(e) => {
        const [parent, leaf] = e.target.value.split('\u0000')
        void saveFields(p, { categoryParent: parent, category: leaf })
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur()
          if (p.id != null) focusNextField(p.id, 'category')
        }
      }}
      className={categorySelect}
      aria-label={`Категория «${p.name}»`}
    >
      <option value={`${parentOf(p)}\u0000${leafOf(p)}`}>
        {parentOf(p) === leafOf(p) ? leafOf(p) : `${parentOf(p)} / ${leafOf(p)}`}
      </option>
      {choices
        .filter(
          (choice) =>
            !(
              choice.parent === parentOf(p) &&
              (choice.leaf ?? leafOf(p)) === leafOf(p)
            )
        )
        .map((choice) => (
          <option
            key={`${p.id}-${choice.value}`}
            value={`${choice.parent}\u0000${choice.leaf ?? choice.parent}`}
          >
            {choice.label}
          </option>
        ))}
    </select>
  )

  const hasStock = products.some((p) => Boolean(p.stock))
  const nameWidth = `${nameShare}%`
  const categoryWidth = `${FLEX_SHARE - nameShare}%`

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="m-0 text-small text-muted">
          {trashMode ? 'Корзина' : 'Показано'} {shownRows.length} из {rows.length}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {!trashMode && (
            <button
              type="button"
              className="cursor-pointer whitespace-nowrap rounded-card border border-ok bg-ok px-3 py-1.5 text-small font-semibold text-white hover:bg-okDark"
              onClick={() => {
                setCreateDraft(EMPTY_CREATE)
                setCreateOpen(true)
              }}
            >
              + Товар
            </button>
          )}
          <button
            type="button"
            className="cursor-pointer whitespace-nowrap text-tiny font-semibold text-muted hover:text-ink"
            onClick={resetWidths}
            title="Вернуть стандартную ширину колонок"
          >
            Сбросить ширины
          </button>
        </div>
      </div>

      {message && <p className={okBanner}>{message}</p>}
      {error && <p className={errBanner}>{error}</p>}

      <div className={filterBar}>
        <input
          type="text"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder="Поиск по наименованию, штрихкоду, цене…"
          className={cx(credInput, 'flex-1 min-w-[220px]')}
        />
        <select
          value={filterChoice}
          onChange={(e) => setFilterChoice(e.target.value)}
          className={cx(credInput, 'max-w-[320px]')}
          aria-label="Фильтр по категории"
        >
          <option value="all">Все категории ({products.length})</option>
          {choices.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-3 flex items-center gap-2 overflow-x-auto pb-1">
        <label className={cx(filterChip, 'shrink-0')}>
          <input
            type="checkbox"
            checked={filterWeight}
            onChange={(e) => setFilterWeight(e.target.checked)}
            className="h-4 w-4 cursor-pointer accent-ok"
          />
          <Scales size={16} /> Только весовые
        </label>
        <label className={cx(filterChip, 'shrink-0')}>
          <input
            type="checkbox"
            checked={filterMarked}
            onChange={(e) => setFilterMarked(e.target.checked)}
            className="h-4 w-4 cursor-pointer accent-ok"
          />
          <Marking size={16} /> Только маркированные
        </label>
        <label className={cx(filterChip, 'shrink-0')}>
          <input
            type="checkbox"
            checked={filterOnPos}
            onChange={(e) => setFilterOnPos(e.target.checked)}
            className="h-4 w-4 cursor-pointer accent-ok"
          />
          Только созданные на кассе
        </label>
        <label className={cx(filterChip, 'shrink-0')}>
          <input
            type="checkbox"
            checked={filterInactive}
            onChange={(e) => setFilterInactive(e.target.checked)}
            className="h-4 w-4 cursor-pointer accent-ok"
          />
          Не активные
        </label>
        <button
          type="button"
          className={cx(
            'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-pill px-3 py-1.5 text-small font-semibold transition-colors',
            trashMode
              ? 'border border-ink bg-ink text-white'
              : 'bg-soft text-ink hover:text-ink'
          )}
          onClick={() => setTrashMode((value) => !value)}
          aria-pressed={trashMode}
        >
          <Trash size={16} /> Удалённые ({trash.length})
        </button>
        {trashMode && trash.length > 0 && (
          <button
            type="button"
            className="shrink-0 cursor-pointer whitespace-nowrap rounded-pill border border-warnLine bg-red-50 px-3 py-1.5 text-small font-semibold text-dangerDark"
            onClick={() => void purgeTrash()}
          >
            Очистить корзину
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <p className={hint}>
          {trashMode
            ? 'Корзина пуста.'
            : products.length === 0
              ? 'В активной версии пока нет товаров.'
              : 'Ничего не найдено по заданным фильтрам.'}
        </p>
      ) : isMobile ? (
        /* Мобильный UI: карточки-аккордеоны вместо таблицы. */
        <div className="flex flex-col gap-2">
          {shownRows.map((p) => {
            const expanded = expandedId === p.id
            return (
              <div
                key={p.id}
                className="rounded-card border border-line bg-white"
                data-testid={`catalog-card-${p.id}`}
              >
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
                  onClick={() => setExpandedId(expanded ? null : p.id ?? null)}
                  aria-expanded={expanded}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold text-ink">
                      {p.name}
                    </span>
                    <span className="block truncate text-tiny text-muted">
                      {parentOf(p) === leafOf(p)
                        ? leafOf(p)
                        : `${parentOf(p)} / ${leafOf(p)}`}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block font-bold tabular-nums text-info">
                      {money(p.price)}
                    </span>
                    <span className="block text-tiny text-muted">
                      № {p.categoryOrdinal ?? '—'}
                    </span>
                  </span>
                </button>
                {expanded && (
                  <div className="flex flex-col gap-2 border-t border-line px-3 py-3">
                    {trashMode ? (
                      <>
                        <p className="m-0 text-small text-muted">
                          Штрихкод: {p.barcode || '—'}
                        </p>
                        <button
                          type="button"
                          className="min-h-10 cursor-pointer rounded-md border border-lineStrong bg-white px-3 py-2 text-small font-semibold text-ink"
                          onClick={() => void restoreFromTrash(p)}
                        >
                          Восстановить
                        </button>
                      </>
                    ) : (
                      <>
                        <label className={fieldLabel}>
                          Наименование
                          <input
                            key={`m-name-${p.id}-${p.name}`}
                            className={input}
                            defaultValue={p.name}
                            onBlur={(e) => void saveFields(p, { name: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.currentTarget.blur()
                            }}
                          />
                        </label>
                        <label className={fieldLabel}>
                          Категория
                          {renderCategorySelect(p)}
                        </label>
                        <label className={fieldLabel}>
                          Штрихкод
                          <input
                            key={`m-barcode-${p.id}-${p.barcode ?? ''}`}
                            className={input}
                            defaultValue={p.barcode ?? ''}
                            placeholder="—"
                            onBlur={(e) => void saveFields(p, { barcode: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.currentTarget.blur()
                            }}
                          />
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <label className={fieldLabel}>
                            Цена, ₽
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              className={input}
                              defaultValue={p.price}
                              onBlur={(e) => void onPriceBlur(p, e.target.value)}
                            />
                          </label>
                          <label className={fieldLabel}>
                            Мин. остаток
                            <input
                              type="number"
                              min={0}
                              step="0.5"
                              className={input}
                              defaultValue={p.minStock ?? ''}
                              placeholder={String(storeThreshold)}
                              onBlur={(e) => void onMinStockBlur(p, e.target.value)}
                            />
                          </label>
                        </div>
                        <label className="flex items-center gap-2 text-small">
                          <input
                            type="checkbox"
                            checked={p.requiresMarking}
                            onChange={() =>
                              void saveFields(p, { requiresMarking: !p.requiresMarking })
                            }
                            className="h-4 w-4 accent-ok"
                          />
                          Маркировка
                        </label>
                        <label className="flex items-center gap-2 text-small">
                          <input
                            type="checkbox"
                            checked={p.isActive !== false}
                            onChange={(e) => void onActiveToggle(p, e.target.checked)}
                            className="h-4 w-4 accent-ok"
                          />
                          Активен (показывать на кассе)
                        </label>
                        <button
                          type="button"
                          className="min-h-10 cursor-pointer rounded-md border border-warnLine bg-red-50 px-3 py-2 text-small font-semibold text-dangerDark"
                          onClick={() => void moveToTrash(p)}
                        >
                          В корзину
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <div className={tableWrap}>
          <table ref={tableRef} className={cx(table, 'table-fixed')}>
            <colgroup>
              <col style={{ width: nameWidth }} />
              <col style={{ width: categoryWidth }} />
              <col style={{ width: '4ch' }} />
              <col style={{ width: `${barcodeCh}ch` }} />
              <col style={{ width: `${priceCh}ch` }} />
              {!trashMode && <col style={{ width: '11ch' }} />}
              {hasStock && <col style={{ width: '7ch' }} />}
              <col style={{ width: '96px' }} />
              {!trashMode && <col style={{ width: '72px' }} />}
              <col style={{ width: '96px' }} />
            </colgroup>
            <thead>
              <tr>
                <th className={cx(th, 'whitespace-nowrap')}>
                  <span className="relative block w-full">
                    {trashMode ? 'Удалённый товар' : 'Наименование'}
                    {!trashMode && (
                      <span
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Изменить ширину колонок"
                        onPointerDown={onResizeStart}
                        className="absolute -right-1.5 top-0 flex h-full w-3 cursor-col-resize touch-none items-center justify-center text-muted hover:text-ink"
                        title="Потяните, чтобы изменить ширину"
                      >
                        ⠿
                      </span>
                    )}
                  </span>
                </th>
                <th className={cx(th, 'whitespace-nowrap')}>Категория</th>
                <th className={cx(thNum, 'whitespace-nowrap')}>№</th>
                <th className={cx(th, 'whitespace-nowrap')}>Штрихкод</th>
                <th className={cx(thNum, 'whitespace-nowrap')}>Цена, ₽</th>
                {!trashMode && (
                  <th className={cx(thNum, 'whitespace-nowrap')}>Мин. остаток</th>
                )}
                {hasStock && <th className={cx(thNum, 'whitespace-nowrap')}>Остаток</th>}
                <th className={cx(th, 'whitespace-nowrap')}>Маркировка</th>
                {!trashMode && (
                  <th className={cx(th, 'whitespace-nowrap')}>Активен</th>
                )}
                <th className={cx(th, 'whitespace-nowrap')}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {shownRows.map((p) => (
                <tr key={p.id}>
                  <td className={cx(td, 'relative')}>
                    <div className="flex flex-col items-start gap-1">
                      {trashMode ? (
                        <span className="truncate font-semibold" title={p.name}>
                          {p.name}
                        </span>
                      ) : (
                        <input
                          key={`name-${p.id}-${p.name}`}
                          data-row={p.id}
                          data-field="name"
                          defaultValue={p.name}
                          onFocus={(e) => {
                            setFocusedNameId(p.id ?? null)
                            const el = e.currentTarget
                            el.style.width = `${Math.max(14, p.name.length + 2)}ch`
                            el.style.position = 'absolute'
                            el.style.zIndex = '30'
                          }}
                          onBlur={(e) => {
                            setFocusedNameId(null)
                            const el = e.currentTarget
                            el.style.width = ''
                            el.style.position = ''
                            el.style.zIndex = ''
                            void saveFields(p, { name: el.value })
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              e.currentTarget.blur()
                              if (p.id != null) focusNextField(p.id, 'name')
                            }
                          }}
                          className={cx(
                            nameInput,
                            focusedNameId === p.id && 'bg-white shadow-modal'
                          )}
                          aria-label={`Наименование «${p.name}»`}
                        />
                      )}
                      {(p.isWeight || p.unit === 'кг') && (
                        <span className={badgeWeight}>
                          <Scales size={16} /> кг
                        </span>
                      )}
                      {p.isCreatedOnPos && (
                        <span className={badgePosCreated}>Создан на кассе</span>
                      )}
                    </div>
                  </td>
                  <td className={td}>
                    {trashMode ? (
                      <span
                        className="block truncate text-small"
                        title={`${parentOf(p)} / ${leafOf(p)}`}
                      >
                        {parentOf(p) === leafOf(p)
                          ? leafOf(p)
                          : `${parentOf(p)} / ${leafOf(p)}`}
                      </span>
                    ) : (
                      renderCategorySelect(p)
                    )}
                  </td>
                  <td className={tdNum}>{p.categoryOrdinal ?? '—'}</td>
                  <td className={td}>
                    {trashMode ? (
                      <span className="font-mono text-tiny">{p.barcode || '—'}</span>
                    ) : (
                      <input
                        key={`barcode-${p.id}-${p.barcode ?? ''}`}
                        data-row={p.id}
                        data-field="barcode"
                        defaultValue={p.barcode ?? ''}
                        onBlur={(e) => void saveFields(p, { barcode: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            e.currentTarget.blur()
                            if (p.id != null) focusNextField(p.id, 'barcode')
                          }
                        }}
                        className={barcodeInput}
                        placeholder="—"
                        aria-label={`Штрихкод «${p.name}»`}
                      />
                    )}
                  </td>
                  <td className={tdNum}>
                    {trashMode ? (
                      <span className="text-small tabular-nums">{money(p.price)}</span>
                    ) : (
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        data-row={p.id}
                        data-field="price"
                        defaultValue={p.price}
                        onBlur={(e) => void onPriceBlur(p, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            e.currentTarget.blur()
                            if (p.id != null) focusNextField(p.id, 'price')
                          }
                        }}
                        className={priceField}
                      />
                    )}
                  </td>
                  {!trashMode && (
                    <td className={tdNum}>
                      <input
                        type="number"
                        min={0}
                        step="0.5"
                        data-row={p.id}
                        data-field="minStock"
                        defaultValue={p.minStock ?? ''}
                        placeholder={String(storeThreshold)}
                        title="Пусто — общий порог магазина"
                        onBlur={(e) => void onMinStockBlur(p, e.target.value)}
                        className={priceField}
                      />
                    </td>
                  )}
                  {hasStock && <td className={tdNum}>{p.stock}</td>}
                  <td className={td}>
                    <span className={p.requiresMarking ? badgeYes : badgeNo}>
                      {p.requiresMarking ? 'Да' : 'Нет'}
                    </span>
                  </td>
                  {!trashMode && (
                    <td className={td}>
                      <input
                        type="checkbox"
                        checked={p.isActive !== false}
                        onChange={(e) => void onActiveToggle(p, e.target.checked)}
                        className="h-4 w-4 cursor-pointer accent-ok"
                        title="Деактивация скрывает товар с кассы, номер сохраняется"
                      />
                    </td>
                  )}
                  <td className={td}>
                    {trashMode ? (
                      <button
                        type="button"
                        className="cursor-pointer whitespace-nowrap rounded-md border border-lineStrong bg-white px-2 py-1 text-tiny font-semibold text-ink hover:bg-soft"
                        onClick={() => void restoreFromTrash(p)}
                      >
                        Восстановить
                      </button>
                    ) : (
                      <IconButton
                        tone="danger"
                        size="sm"
                        label={`В корзину: ${p.name}`}
                        onClick={() => void moveToTrash(p)}
                      >
                        <Close size={16} />
                      </IconButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > shownRows.length && (
        <div className="mt-2.5 flex justify-center">
          <button
            type="button"
            className="cursor-pointer rounded-card border border-lineStrong bg-white px-4 py-1.5 text-small font-semibold text-ink hover:bg-soft"
            onClick={() => setVisibleLimit((limit) => limit + PAGE_SIZE)}
          >
            Показать ещё {Math.min(PAGE_SIZE, rows.length - shownRows.length)}
          </button>
        </div>
      )}

      {createOpen && (
        <div
          className={cx(overlay, 'z-modal')}
          role="dialog"
          aria-modal="true"
          aria-label="Новый товар"
        >
          <form className={modalSm} onSubmit={submitCreate}>
            <h3 className="mt-0 text-big font-bold">Новый товар</h3>

            <label className={fieldLabel}>
              Наименование
              <input
                className={input}
                value={createDraft.name}
                onChange={(e) =>
                  setCreateDraft((d) => ({ ...d, name: e.target.value }))
                }
                placeholder="Например, Сосиски молочные"
                autoFocus
              />
            </label>

            <div className="grid gap-2 sm:grid-cols-2">
              <label className={fieldLabel}>
                Цена, ₽
                <input
                  className={input}
                  type="number"
                  min={0}
                  step="0.01"
                  value={createDraft.price}
                  onChange={(e) =>
                    setCreateDraft((d) => ({ ...d, price: e.target.value }))
                  }
                  placeholder="0,00"
                />
              </label>
              <label className={fieldLabel}>
                Штрихкод
                <input
                  className={input}
                  value={createDraft.barcode}
                  onChange={(e) =>
                    setCreateDraft((d) => ({ ...d, barcode: e.target.value }))
                  }
                  placeholder="необязательно"
                />
              </label>
            </div>

            <label className={fieldLabel}>
              Категория
              <select
                className={input}
                value={createDraft.categoryValue}
                onChange={(e) =>
                  setCreateDraft((d) => ({ ...d, categoryValue: e.target.value }))
                }
              >
                <option value="all">Общее</option>
                {choices.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="mb-2 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={createDraft.isWeight}
                onChange={(e) =>
                  setCreateDraft((d) => ({ ...d, isWeight: e.target.checked }))
                }
                className="h-4 w-4 accent-ok"
              />
              Весовой (цена за кг)
            </label>
            <label className="mb-4 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={createDraft.requiresMarking}
                onChange={(e) =>
                  setCreateDraft((d) => ({
                    ...d,
                    requiresMarking: e.target.checked
                  }))
                }
                className="h-4 w-4 accent-ok"
              />
              Требует маркировки
            </label>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="cursor-pointer rounded-card border border-lineStrong bg-white px-4 py-2 text-sm font-semibold text-ink hover:bg-soft"
                onClick={() => setCreateOpen(false)}
                disabled={createBusy}
              >
                Отмена
              </button>
              <button
                type="submit"
                className="cursor-pointer rounded-card border border-ok bg-ok px-4 py-2 text-sm font-semibold text-white hover:bg-okDark disabled:opacity-50"
                disabled={createBusy}
              >
                Создать
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
